import { Controller } from "@hotwired/stimulus";

/**
 * Sticky CTA Controller
 *
 * Shows a fixed CTA button at the bottom of the screen on mobile
 * when the inline CTA button scrolls out of the viewport.
 *
 * Uses hysteresis pattern to prevent flickering and CSS variables
 * for Safari iOS dynamic toolbar compatibility.
 */
export default class extends Controller {
  static targets = ["inlineButton", "stickyButton"];
  static values = {
    breakpoint: { type: Number, default: 768 },
  };

  connect() {
    this.isSticky = false;
    this.scrollTicking = false;
    this.mediaQuery = window.matchMedia(
      `(max-width: ${this.breakpointValue}px)`
    );

    // Bind methods
    this.handleMediaChange = this.handleMediaChange.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.checkPosition = this.checkPosition.bind(this);
    this.updateViewportHeight = this.updateViewportHeight.bind(this);

    // Set initial viewport height CSS variable
    this.updateViewportHeight();

    // Initial setup based on current viewport
    this.handleMediaChange(this.mediaQuery);

    // Listen for viewport changes
    this.mediaQuery.addEventListener("change", this.handleMediaChange);

    // Listen for resize events to update CSS variable (Safari dynamic toolbar fix)
    window.addEventListener("resize", this.updateViewportHeight);

    // Also listen to visualViewport for more accurate Safari tracking
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", this.updateViewportHeight);
    }
  }

  disconnect() {
    this.removeScrollListener();
    this.mediaQuery.removeEventListener("change", this.handleMediaChange);
    window.removeEventListener("resize", this.updateViewportHeight);

    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this.updateViewportHeight);
    }
  }

  // Update CSS variable with actual viewport height (fixes Safari iOS dynamic toolbar)
  updateViewportHeight() {
    // Use visualViewport if available (more accurate on iOS Safari)
    const height = window.visualViewport
      ? window.visualViewport.height
      : window.innerHeight;

    document.documentElement.style.setProperty("--viewport-height", `${height}px`);
  }

  handleMediaChange(e) {
    if (e.matches) {
      // Mobile: Setup scroll listener
      this.setupScrollListener();
      // Check initial position
      this.checkPosition();
    } else {
      // Desktop: Remove listener and hide sticky
      this.removeScrollListener();
      this.hideStickyButton();
    }
  }

  setupScrollListener() {
    if (!this.hasInlineButtonTarget) return;
    window.addEventListener("scroll", this.handleScroll, { passive: true });
  }

  removeScrollListener() {
    window.removeEventListener("scroll", this.handleScroll);
  }

  handleScroll() {
    // Throttle with requestAnimationFrame for 60fps
    if (!this.scrollTicking) {
      this.scrollTicking = true;
      requestAnimationFrame(() => {
        this.checkPosition();
        this.scrollTicking = false;
      });
    }
  }

  checkPosition() {
    if (!this.hasInlineButtonTarget || !this.hasStickyButtonTarget) return;

    const rect = this.inlineButtonTarget.getBoundingClientRect();

    // Hysteresis thresholds to prevent flickering (in pixels)
    const showThreshold = 50;
    const hideThreshold = 100;

    const buttonBottom = rect.bottom;

    if (this.isSticky) {
      if (buttonBottom > hideThreshold) {
        this.hideStickyButton();
      }
    } else {
      if (buttonBottom < showThreshold) {
        this.showStickyButton();
      }
    }
  }

  showStickyButton() {
    if (!this.hasStickyButtonTarget || this.isSticky) return;

    this.isSticky = true;
    this.stickyButtonTarget.classList.add("is-visible");
    this.stickyButtonTarget.setAttribute("aria-hidden", "false");
  }

  hideStickyButton() {
    if (!this.hasStickyButtonTarget || !this.isSticky) return;

    this.isSticky = false;
    this.stickyButtonTarget.classList.remove("is-visible");
    this.stickyButtonTarget.setAttribute("aria-hidden", "true");
  }
}
